import React, { FC, useState } from 'react'
import NewQuestionSetEditor from 'question-sets/components/NewQuestionSetEditor'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import ConfirmQuestionnaireVersionDialog from 'surveys/components/ConfirmQuestionnaireVersionDialog'
import { Redirect } from 'react-router-dom'
import PageHeader from 'App/components/PageHeader'
import { QuestionSetResponse } from 'civic-champs-shared/question-sets/types'
import isUndefined from 'lodash/isUndefined'
import { useBroadcastChannel } from '../../civic-champs-shared/core/hooks/useBroadcastChannel'
import { SurveyVersionContext } from 'surveys/utils/getSurveyVersionContext'

export interface QuestionSetEditorComponentProps {
  questionSet?: QuestionSetResponse
  isSubmitting: boolean
  onSubmit: (...props: any) => Promise<any>
  backLink: string
  backText: string
  title: string
  redirectLink?: string
  channel?: string | null
  surveyVersionContext?: SurveyVersionContext
  enableUnsavedChangesGuard?: boolean
}

const QuestionSetEditorComponent: FC<Omit<QuestionSetEditorComponentProps, 'title'>> = ({
  questionSet,
  isSubmitting,
  onSubmit,
  backLink,
  backText,
  redirectLink,
  channel,
  surveyVersionContext,
  enableUnsavedChangesGuard = false,
}) => {
  const { postMessage } = useBroadcastChannel(channel)
  const showConfirmationDialog = useShowPrompt(ConfirmQuestionnaireVersionDialog)
  const [redirect, setRedirect] = useState<boolean>(false)
  const isNew = isUndefined(questionSet)
  const handleSubmit = async (payload: any): Promise<void> => {
    if (!isNew && payload.questionsEdited) {
      const confirmation = await showConfirmationDialog({
        newVersion: surveyVersionContext?.newVersion ?? 2,
        minimumRequiredVersion: surveyVersionContext?.minimumRequiredVersion ?? 1,
        showMinimumRequiredChoice: surveyVersionContext?.showMinimumRequiredChoice ?? false,
      })
      if (!confirmation) return

      const updateMinimumRequiredVersion =
        typeof confirmation === 'object' ? confirmation.updateMinimumRequiredVersion : true
      payload = { ...payload, updateMinimumRequiredVersion }
    }
    const result = await onSubmit(payload)
    if (channel) {
      postMessage(result)
      window.close()
    } else {
      setRedirect(true)
    }
  }

  if (redirect) {
    return <Redirect to={`${redirectLink || backLink}`} />
  }

  return (
    <NewQuestionSetEditor
      questionSet={questionSet}
      isNew={isNew}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      backLink={backLink}
      backText={backText}
      channel={channel}
      enableUnsavedChangesGuard={enableUnsavedChangesGuard}
    />
  )
}

export const QuestionSetEditor: FC<QuestionSetEditorComponentProps> = ({ title, ...props }) => {
  return (
    <PageHeader title={title}>
      <QuestionSetEditorComponent {...props} />
    </PageHeader>
  )
}

export default QuestionSetEditor
